/**
 * Download video from URL with proper filename
 */
export async function downloadVideo(url: string, filename?: string): Promise<void> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || `vidgentf-video-${Date.now()}.mp4`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Failed to download video:', error);
    throw new Error('Video download mislukt. Probeer het opnieuw.');
  }
}

/**
 * Share video using Web Share API with fallback to clipboard
 */
export async function shareVideo(
  url: string,
  title: string = 'VidGenTF Video',
  text: string = 'Bekijk deze verkeerssituatie video'
): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({
        title,
        text,
        url,
      });
    } else {
      await navigator.clipboard.writeText(url);
      alert('Video link gekopieerd naar klembord!');
    }
  } catch (error: any) {
    if (error.name !== 'AbortError') {
      console.error('Failed to share video:', error);
      throw new Error('Video delen mislukt. Probeer de link handmatig te kopiëren.');
    }
  }
}

/**
 * Generate thumbnail from video URL by extracting first frame
 */
export async function generateThumbnail(
  videoUrl: string,
  seekTime: number = 0.5
): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        video.currentTime = seekTime;
      };

      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Canvas context not available');
          }

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const thumbnail = canvas.toDataURL('image/jpeg', 0.85);
          resolve(thumbnail);

          video.remove();
        } catch (error) {
          reject(error);
        }
      };

      video.onerror = () => {
        reject(new Error('Failed to load video for thumbnail generation'));
      };

      video.src = videoUrl;
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
      reject(new Error('Thumbnail generatie mislukt'));
    }
  });
}

/**
 * Download thumbnail as image file
 */
export async function downloadThumbnail(
  videoUrl: string,
  filename?: string
): Promise<void> {
  try {
    const thumbnailDataUrl = await generateThumbnail(videoUrl);

    const link = document.createElement('a');
    link.href = thumbnailDataUrl;
    link.download = filename || `vidgentf-thumbnail-${Date.now()}.jpg`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Failed to download thumbnail:', error);
    throw new Error('Thumbnail download mislukt. Probeer het opnieuw.');
  }
}

/**
 * Format video duration in seconds to readable time string
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return remainingSeconds > 0
    ? `${minutes}m ${remainingSeconds}s`
    : `${minutes}m`;
}

/**
 * Get video file size estimate based on duration
 * Rough estimate: ~1MB per second for HD video
 */
export function estimateVideoSize(durationSeconds: number): string {
  const sizeInMB = durationSeconds * 1.0;

  if (sizeInMB < 1) {
    return `~${(sizeInMB * 1024).toFixed(0)} KB`;
  }

  return `~${sizeInMB.toFixed(1)} MB`;
}
